import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, Loader2, Search, Users } from 'lucide-react'
import { usePatients } from '../contexts/PatientsContext'
import { useTheme } from '../contexts/ThemeContext'
import { PRIOR_AUTH_NAV, ROUTES, patientDetailPath } from '../routes/routeMap'
import {
  PRIOR_AUTH_GRID_LABEL,
  PRIOR_AUTH_GRID_OPTIONS,
  STATUS_LABEL,
  calculateAge,
  formatPatientDate,
  patientAvatarColors,
  patientFullName,
  patientInitials,
  patientMemberIdLabel,
  patientSummary,
  priorAuthCreatePrefillFromPatient,
  resolvePriorAuthGridStatus,
  type PatientPriorAuthGridStatus,
  type PatientStatus,
} from '../data/patientData'

type PriorAuthFilter = PatientPriorAuthGridStatus | 'all'
type PayorFilter = string | 'all'

function priorAuthBadgeClass(status: PatientPriorAuthGridStatus): string {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-700'
    case 'pending_verification':
      return 'bg-amber-100 text-amber-800 hover:bg-amber-200 cursor-pointer'
    case 'inactive':
    default:
      return 'bg-gray-100 text-gray-600'
  }
}

function statusBadgeClass(status: PatientStatus): string {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-700'
    case 'on_hold':
      return 'bg-amber-100 text-amber-700'
    case 'pending':
      return 'bg-blue-100 text-blue-700'
    case 'inactive':
    default:
      return 'bg-gray-100 text-gray-600'
  }
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

function StatCard({
  value,
  label,
}: {
  value: string
  label: string
  emphasis?: boolean
}) {
  const { theme } = useTheme()

  return (
    <div className="flex-1 min-w-[140px] px-2">
      <p
        className="text-2xl sm:text-3xl font-bold text-center"
        style={{ color: theme.colors.textPrimary }}
      >
        {value}
      </p>
      <p className="text-xs sm:text-sm text-center mt-1" style={{ color: theme.colors.textSecondary }}>
        {label}
      </p>
    </div>
  )
}

const PatientsDirectory = () => {
  const { patients, isLoadingPatients, patientsLoadError, refreshPatients } = usePatients()
  const { theme } = useTheme()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [priorAuthFilter, setPriorAuthFilter] = useState<PriorAuthFilter>('all')
  const [payorFilter, setPayorFilter] = useState<PayorFilter>('all')

  useEffect(() => {
    void refreshPatients()
  }, [refreshPatients])

  const payorOptions = useMemo(() => {
    const set = new Set<string>()
    patients.forEach((p) => set.add(p.primaryPayor))
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [patients])

  const filteredPatients = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    return patients.filter((p) => {
      if (
        priorAuthFilter !== 'all' &&
        resolvePriorAuthGridStatus(p) !== priorAuthFilter
      ) {
        return false
      }
      if (payorFilter !== 'all' && p.primaryPayor !== payorFilter) return false
      if (!term) return true
      return (
        patientFullName(p).toLowerCase().includes(term) ||
        p.patientId.toLowerCase().includes(term) ||
        p.memberId.toLowerCase().includes(term) ||
        p.primaryPayor.toLowerCase().includes(term) ||
        (p.subscriberRelationship ?? '').toLowerCase().includes(term)
      )
    })
  }, [patients, searchTerm, priorAuthFilter, payorFilter])

  const summary = useMemo(() => patientSummary(patients), [patients])

  const cardStyle = {
    backgroundColor: theme.colors.cardBackground,
    border: `1px solid ${theme.colors.cardBorder}`,
  }

  const inputStyle = {
    backgroundColor: theme.colors.inputBackground,
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.textPrimary,
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex items-start gap-3">
        <div
          className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: theme.colors.primaryLight, color: theme.colors.primary }}
        >
          <Users size={20} />
        </div>
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>
            Patients
          </h1>
          <p className="mt-1 text-sm sm:text-base" style={{ color: theme.colors.textSecondary }}>
            Manage patient records, coverage, and equipment assignments
          </p>
        </div>
      </div>

      <div className="rounded-lg shadow-sm px-2 sm:px-4 py-4 sm:py-5" style={cardStyle}>
        <div
          className="flex flex-wrap items-stretch"
          style={{
            borderColor: theme.colors.border,
          }}
        >
          <StatCard value={summary.activePatients.toString()} label="Active patients" emphasis />
          <StatCard value={summary.pendingEligibility.toString()} label="Pending eligibility" />
          <StatCard value={summary.expiringSoon.toString()} label="Expiring soon" />
          <StatCard value={summary.coverageGaps.toString()} label="Coverage gaps" />
          <StatCard value={formatCurrency(summary.openPatientAr)} label="Open patient AR" />
        </div>
      </div>

      <div className="rounded-lg shadow-sm overflow-hidden" style={cardStyle}>
        <div
          className="px-4 sm:px-6 py-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"
          style={{ borderBottom: `1px solid ${theme.colors.border}` }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <h2 className="text-base font-semibold" style={{ color: theme.colors.textPrimary }}>
              All patients
            </h2>
            <span
              className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
              style={{
                backgroundColor: theme.colors.tableRowHover,
                color: theme.colors.textSecondary,
              }}
            >
              {patients.length} records
            </span>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative w-full sm:w-72">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: theme.colors.textSecondary }}
              />
              <input
                type="text"
                placeholder="Search by name, ID, or payor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                style={inputStyle}
              />
            </div>
            <select
              value={priorAuthFilter}
              onChange={(e) => setPriorAuthFilter(e.target.value as PriorAuthFilter)}
              className="rounded-lg px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500"
              style={inputStyle}
              aria-label="Filter by prior auth status"
            >
              {PRIOR_AUTH_GRID_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              value={payorFilter}
              onChange={(e) => setPayorFilter(e.target.value as PayorFilter)}
              className="rounded-lg px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500"
              style={inputStyle}
              aria-label="Filter by payor"
            >
              <option value="all">All payors</option>
              {payorOptions.map((payor) => (
                <option key={payor} value={payor}>
                  {payor}
                </option>
              ))}
            </select>
          </div>
        </div>

        {isLoadingPatients && (
          <div className="px-4 sm:px-6 py-3 border-b border-gray-200 bg-blue-50 text-blue-700 text-sm flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" />
            Loading patients from API...
          </div>
        )}

        {patientsLoadError && (
          <div className="px-4 sm:px-6 py-3 border-b border-gray-200 bg-amber-50 text-amber-800 text-sm flex items-center gap-2">
            <AlertCircle size={16} />
            Unable to load patients. {patientsLoadError}
          </div>
        )}

        {filteredPatients.length === 0 ? (
          <div className="px-4 sm:px-6 py-12 text-center">
            <p className="text-sm" style={{ color: theme.colors.textSecondary }}>
              {searchTerm || priorAuthFilter !== 'all' || payorFilter !== 'all'
                ? 'No patients match your filters.'
                : 'No patients yet.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1180px]">
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.colors.tableBorder}`, backgroundColor: theme.colors.tableHeaderBackground }}>
                  {['Patient', 'Relationship', 'DOB / Age', 'Primary payor', 'Member ID', 'Last verified', 'Prior auth', 'Status'].map(
                    (header) => (
                      <th
                        key={header}
                        className="px-4 sm:px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider"
                        style={{ color: theme.colors.tableHeaderText }}
                      >
                        {header}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredPatients.map((patient) => {
                  const colors = patientAvatarColors(patient)
                  const age = calculateAge(patient.dateOfBirth)
                  const priorAuthStatus = resolvePriorAuthGridStatus(patient)
                  const openPriorAuthCreate = () => {
                    navigate(
                      ROUTES.PRIOR_AUTH,
                      {
                        state: PRIOR_AUTH_NAV.create(
                          priorAuthCreatePrefillFromPatient(patient),
                          1
                        ),
                      }
                    )
                  }
                  return (
                    <tr
                      key={patient.id}
                      onClick={() => navigate(patientDetailPath(patient.id))}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          navigate(patientDetailPath(patient.id))
                        }
                      }}
                      className="transition-colors cursor-pointer focus:outline-none"
                      style={{
                        borderTop: `1px solid ${theme.colors.tableBorder}`,
                        backgroundColor: theme.colors.tableRowBackground,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme.colors.tableRowHover
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = theme.colors.tableRowBackground
                      }}
                    >
                      <td className="px-4 sm:px-6 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${colors.bg} ${colors.text}`}
                            aria-hidden
                          >
                            {patientInitials(patient)}
                          </div>
                          <div className="min-w-0">
                            <p
                              className="text-sm font-medium truncate"
                              style={{ color: theme.colors.textPrimary }}
                            >
                              {patientFullName(patient)}
                            </p>
                            {patientMemberIdLabel(patient) ? (
                              <p
                                className="text-xs truncate font-mono"
                                style={{ color: theme.colors.textSecondary }}
                              >
                                {patientMemberIdLabel(patient)}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td
                        className="px-4 sm:px-6 py-3 text-sm whitespace-nowrap"
                        style={{ color: theme.colors.textSecondary }}
                      >
                        {patient.subscriberRelationship || '—'}
                      </td>
                      <td
                        className="px-4 sm:px-6 py-3 text-sm whitespace-nowrap"
                        style={{ color: theme.colors.textSecondary }}
                      >
                        {formatPatientDate(patient.dateOfBirth)}
                        <span> · {age}</span>
                      </td>
                      <td
                        className="px-4 sm:px-6 py-3 text-sm"
                        style={{ color: theme.colors.textSecondary }}
                      >
                        {patient.primaryPayor}
                      </td>
                      <td
                        className="px-4 sm:px-6 py-3 text-sm font-mono"
                        style={{ color: theme.colors.textSecondary }}
                      >
                        {patient.memberId}
                      </td>
                      <td
                        className="px-4 sm:px-6 py-3 text-sm whitespace-nowrap"
                        style={{ color: theme.colors.textSecondary }}
                      >
                        {formatPatientDate(patient.lastVerified)}
                      </td>
                      <td className="px-4 sm:px-6 py-3">
                        {priorAuthStatus === 'pending_verification' ? (
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation()
                              openPriorAuthCreate()
                            }}
                            className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full transition-colors ${priorAuthBadgeClass(
                              priorAuthStatus
                            )}`}
                          >
                            {PRIOR_AUTH_GRID_LABEL[priorAuthStatus]}
                          </button>
                        ) : (
                          <span
                            className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full ${priorAuthBadgeClass(
                              priorAuthStatus
                            )}`}
                          >
                            {PRIOR_AUTH_GRID_LABEL[priorAuthStatus]}
                          </span>
                        )}
                      </td>
                      <td className="px-4 sm:px-6 py-3">
                        <span
                          className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full ${statusBadgeClass(
                            patient.status
                          )}`}
                        >
                          {STATUS_LABEL[patient.status]}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {filteredPatients.length > 0 && (
          <div
            className="px-4 sm:px-6 py-3"
            style={{
              borderTop: `1px solid ${theme.colors.border}`,
              backgroundColor: theme.colors.tableHeaderBackground,
            }}
          >
            <p className="text-sm" style={{ color: theme.colors.textSecondary }}>
              Showing <span className="font-medium">{filteredPatients.length}</span> of{' '}
              <span className="font-medium">{patients.length}</span> patients
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default PatientsDirectory
